import { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DEFAULT_PAGINATION_LIMIT } from "../../../../../globalConstants";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { useDrawerFilters } from "../../drawer-filters";
import { ActionableItemsFilterConfig, defaultFilterParams } from "../utils/filterConfig";

import { useActionableItems } from "./useActionableItems";

const ACTION_ITEMS_QUERY_OPTIONS = { debounceMs: 0, replaceNavigation: true };

export function useActionableItemsPage({ caseId = null }) {
  const { filters, updateFilter, bulkUpdateFilters } = useQueryParams(ACTION_ITEMS_QUERY_OPTIONS);
  const navigate = useNavigate();
  const location = useLocation();

  const { filterState, activeFilters } = useDrawerFilters(
    ActionableItemsFilterConfig,
    ACTION_ITEMS_QUERY_OPTIONS
  );

  const { actionItems, loading, resultCount, fetchActionItems, toggleActionItemStatus } =
    useActionableItems({ caseId });

  const [isActionItemModalOpen, setIsActionItemModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeActionItem, setActiveActionItem] = useState(null);

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);

  const [search, setSearch] = useState(filters.get("search") || "");
  const ordering = filters.get("ordering") || "due_date";

  useEffect(() => {
    const filtersToSet = Object.entries(defaultFilterParams || {}).filter(([key, _value]) => {
      const currentValue = filters.get(key);
      return currentValue === null || currentValue === undefined;
    });

    if (filtersToSet.length > 0) {
      bulkUpdateFilters(filtersToSet);
    }
  }, []);

  const handlePageChange = useCallback(
    (_event, page) => {
      const newOffset = (page - 1) * DEFAULT_PAGINATION_LIMIT;
      updateFilter("offset", newOffset.toString());
    },
    [updateFilter]
  );

  const handleOrderingChange = useCallback(
    (newOrdering) => {
      updateFilter("ordering", newOrdering);
    },
    [updateFilter]
  );

  const handleSearchChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearch(value);

      const timeoutId = setTimeout(() => {
        if (value) {
          updateFilter("search", value);
        } else {
          updateFilter("search", undefined);
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [updateFilter]
  );

  const handleEdit = useCallback(
    (actionItem) => {
      if (actionItem?.id && actionItem?.formUrl) {
        navigate(`/form/${actionItem.id}`, {
          state: {
            returnTo: {
              pathname: location.pathname,
              search: location.search,
            },
          },
        });
        return;
      }

      setActiveActionItem(actionItem);
      setIsActionItemModalOpen(true);
    },
    [navigate, location.pathname, location.search]
  );

  const handleDelete = useCallback((actionItem) => {
    setActiveActionItem(actionItem);
    setDeleteOpen(true);
  }, []);

  const handleView = useCallback((actionItem) => {
    setActiveActionItem(actionItem);
    setDetailOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setActiveActionItem(null);
    setIsActionItemModalOpen(true);
  }, []);

  const handleToggleStatus = useCallback(
    async (actionItemId, currentStatus) => {
      const result = await toggleActionItemStatus(actionItemId, currentStatus);

      if (result.success) {
        setOpenSuccess(true);
      } else {
        setErrorMessage(result.error);
        setOpenError(true);
      }
    },
    [toggleActionItemStatus]
  );

  const handleNavigateToClient = useCallback(
    (onCase) => {
      navigate(`/client-detail/${onCase?.id ?? onCase?.pk}/actionable-items`);
    },
    [navigate]
  );

  const handleModalSuccess = useCallback(() => {
    fetchActionItems();
    setIsActionItemModalOpen(false);
    setDeleteOpen(false);
    setDetailOpen(false);
    setActiveActionItem(null);
  }, [fetchActionItems]);

  return {
    filterState,
    activeFilters,
    search,
    actionItems,
    loading,
    resultCount,
    ordering,
    isActionItemModalOpen,
    setIsActionItemModalOpen,
    deleteOpen,
    setDeleteOpen,
    detailOpen,
    setDetailOpen,
    activeActionItem,
    setActiveActionItem,
    openSuccess,
    setOpenSuccess,
    openError,
    setOpenError,
    errorMessage,
    handlePageChange,
    handleOrderingChange,
    handleSearchChange,
    handleEdit,
    handleDelete,
    handleView,
    handleCreate,
    handleToggleStatus,
    handleNavigateToClient,
    handleModalSuccess,
  };
}
